/**
 * Tennis court SVGs — clay (textured terracotta), grass (mow stripes), hard (premium navy).
 * Presentation only; geometry matches SportCourtShell viewBoxes.
 */

import { normalizeTennisCourtColour } from "../config/tennisCourtColour";

const LINE_CLAY = "#f7f0e6";
const LINE_GRASS = "#f2f6f1";
const LINE_HARD = "#eef4fa";
const NET_CLAY = "#241812";
const NET_GRASS = "#1a1f1c";
const NET_HARD = "#0e1420";

/** Roland Garros–inspired: dusty terracotta + crushed-brick grain. */
function ClayDefs({ idPrefix }) {
  return (
    <defs>
      <linearGradient id={`${idPrefix}-surround`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#6e3d2c" />
        <stop offset="100%" stopColor="#4f2a1e" />
      </linearGradient>
      <linearGradient id={`${idPrefix}-clay`} x1="0" y1="0" x2="0.18" y2="1">
        <stop offset="0%" stopColor="#c98968" />
        <stop offset="38%" stopColor="#b8704f" />
        <stop offset="72%" stopColor="#a85f41" />
        <stop offset="100%" stopColor="#9a5338" />
      </linearGradient>
      {/* Soft sweep bands — elegant clay “rake” without looking striped like grass */}
      <pattern
        id={`${idPrefix}-clay-sweep`}
        width="28"
        height="28"
        patternUnits="userSpaceOnUse"
      >
        <rect width="28" height="28" fill="#b8704f" />
        <rect width="14" height="28" fill="#c27d5a" fillOpacity="0.55" />
        <rect x="14" width="14" height="28" fill="#a86442" fillOpacity="0.4" />
      </pattern>
      {/* Fine crushed-brick grain */}
      <filter
        id={`${idPrefix}-clay-grain`}
        x="0%"
        y="0%"
        width="100%"
        height="100%"
        filterUnits="objectBoundingBox"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.9"
          numOctaves="3"
          stitchTiles="stitch"
          result="noise"
        />
        <feColorMatrix
          in="noise"
          type="matrix"
          values="0 0 0 0 0.55
                  0 0 0 0 0.32
                  0 0 0 0 0.22
                  0 0 0 0.22 0"
          result="grain"
        />
        <feBlend in="SourceGraphic" in2="grain" mode="soft-light" />
      </filter>
      <radialGradient
        id={`${idPrefix}-clay-glow`}
        cx="50%"
        cy="46%"
        r="68%"
        fx="48%"
        fy="40%"
      >
        <stop offset="0%" stopColor="#dfb08a" stopOpacity="0.28" />
        <stop offset="45%" stopColor="#b8704f" stopOpacity="0" />
        <stop offset="100%" stopColor="#6e3d2c" stopOpacity="0.28" />
      </radialGradient>
    </defs>
  );
}

function GrassDefs({ idPrefix, stripeAlong = "x" }) {
  const stripeW = 14;
  const light = "#4f9a5c";
  const dark = "#458a52";
  const surround = "#2f6b3d";
  return (
    <defs>
      <linearGradient id={`${idPrefix}-surround`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#356f42" />
        <stop offset="100%" stopColor={surround} />
      </linearGradient>
      <pattern
        id={`${idPrefix}-mow`}
        width={stripeAlong === "x" ? stripeW * 2 : 40}
        height={stripeAlong === "x" ? 40 : stripeW * 2}
        patternUnits="userSpaceOnUse"
      >
        {stripeAlong === "x" ? (
          <>
            <rect width={stripeW} height="40" fill={dark} />
            <rect x={stripeW} width={stripeW} height="40" fill={light} />
          </>
        ) : (
          <>
            <rect width="40" height={stripeW} fill={dark} />
            <rect y={stripeW} width="40" height={stripeW} fill={light} />
          </>
        )}
      </pattern>
    </defs>
  );
}

/** Premium midnight / slate hardcourt — soft brushed acrylic texture. */
function HardDefs({ idPrefix, stripeAlong = "x" }) {
  const brushW = 6;
  return (
    <defs>
      <linearGradient id={`${idPrefix}-surround`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1c2a42" />
        <stop offset="100%" stopColor="#101828" />
      </linearGradient>
      <linearGradient id={`${idPrefix}-hard`} x1="0" y1="0" x2="0.12" y2="1">
        <stop offset="0%" stopColor="#4a6f9a" />
        <stop offset="35%" stopColor="#3a5f88" />
        <stop offset="70%" stopColor="#32567c" />
        <stop offset="100%" stopColor="#2a4a6e" />
      </linearGradient>
      <pattern
        id={`${idPrefix}-hard-brush`}
        width={stripeAlong === "x" ? brushW * 2 : 36}
        height={stripeAlong === "x" ? 36 : brushW * 2}
        patternUnits="userSpaceOnUse"
      >
        {stripeAlong === "x" ? (
          <>
            <rect width={brushW} height="36" fill="#456894" fillOpacity="0.55" />
            <rect x={brushW} width={brushW} height="36" fill="#2f5176" fillOpacity="0.45" />
          </>
        ) : (
          <>
            <rect width="36" height={brushW} fill="#456894" fillOpacity="0.55" />
            <rect y={brushW} width="36" height={brushW} fill="#2f5176" fillOpacity="0.45" />
          </>
        )}
      </pattern>
      <filter
        id={`${idPrefix}-hard-grain`}
        x="0%"
        y="0%"
        width="100%"
        height="100%"
        filterUnits="objectBoundingBox"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.75"
          numOctaves="2"
          stitchTiles="stitch"
          result="noise"
        />
        <feColorMatrix
          in="noise"
          type="matrix"
          values="0 0 0 0 0.22
                  0 0 0 0 0.32
                  0 0 0 0 0.48
                  0 0 0 0.14 0"
          result="grain"
        />
        <feBlend in="SourceGraphic" in2="grain" mode="soft-light" />
      </filter>
      <radialGradient
        id={`${idPrefix}-hard-glow`}
        cx="50%"
        cy="48%"
        r="70%"
        fx="50%"
        fy="42%"
      >
        <stop offset="0%" stopColor="#6a8fbc" stopOpacity="0.22" />
        <stop offset="50%" stopColor="#3a5f88" stopOpacity="0" />
        <stop offset="100%" stopColor="#101828" stopOpacity="0.3" />
      </radialGradient>
    </defs>
  );
}

function CourtSurfaceDefs({ surface, idPrefix, stripeAlong }) {
  if (surface === "grass") {
    return <GrassDefs idPrefix={idPrefix} stripeAlong={stripeAlong} />;
  }
  if (surface === "hard") {
    return <HardDefs idPrefix={idPrefix} stripeAlong={stripeAlong} />;
  }
  return <ClayDefs idPrefix={idPrefix} />;
}

function CourtPlayingArea({ surface, idPrefix, left, top, courtW, courtH, line }) {
  if (surface === "grass") {
    return (
      <rect
        x={left}
        y={top}
        width={courtW}
        height={courtH}
        fill={`url(#${idPrefix}-mow)`}
        stroke={line}
        strokeWidth={1.75}
        rx={1}
      />
    );
  }

  if (surface === "hard") {
    return (
      <>
        <rect
          x={left}
          y={top}
          width={courtW}
          height={courtH}
          fill={`url(#${idPrefix}-hard)`}
          stroke={line}
          strokeWidth={1.75}
          rx={1}
        />
        <rect
          x={left}
          y={top}
          width={courtW}
          height={courtH}
          fill={`url(#${idPrefix}-hard-brush)`}
          opacity={0.55}
          rx={1}
          pointerEvents="none"
        />
        <rect
          x={left}
          y={top}
          width={courtW}
          height={courtH}
          fill={`url(#${idPrefix}-hard)`}
          filter={`url(#${idPrefix}-hard-grain)`}
          opacity={0.55}
          rx={1}
          pointerEvents="none"
        />
        <rect
          x={left}
          y={top}
          width={courtW}
          height={courtH}
          fill={`url(#${idPrefix}-hard-glow)`}
          rx={1}
          pointerEvents="none"
        />
      </>
    );
  }

  // Clay — sweep bands + grain + vignette
  return (
    <>
      <rect
        x={left}
        y={top}
        width={courtW}
        height={courtH}
        fill={`url(#${idPrefix}-clay)`}
        stroke={line}
        strokeWidth={1.75}
        rx={1}
      />
      <rect
        x={left}
        y={top}
        width={courtW}
        height={courtH}
        fill={`url(#${idPrefix}-clay-sweep)`}
        opacity={0.42}
        rx={1}
        pointerEvents="none"
      />
      <rect
        x={left}
        y={top}
        width={courtW}
        height={courtH}
        fill={`url(#${idPrefix}-clay)`}
        filter={`url(#${idPrefix}-clay-grain)`}
        opacity={0.65}
        rx={1}
        pointerEvents="none"
      />
      <rect
        x={left}
        y={top}
        width={courtW}
        height={courtH}
        fill={`url(#${idPrefix}-clay-glow)`}
        rx={1}
        pointerEvents="none"
      />
    </>
  );
}

function surfaceLine(surface) {
  if (surface === "grass") return LINE_GRASS;
  if (surface === "hard") return LINE_HARD;
  return LINE_CLAY;
}

function surfaceNet(surface) {
  if (surface === "grass") return NET_GRASS;
  if (surface === "hard") return NET_HARD;
  return NET_CLAY;
}

function portraitGeometry() {
  const w = 200;
  const h = 439;
  const pad = 10;
  const left = pad;
  const top = pad;
  const courtW = w - pad * 2;
  const courtH = h - pad * 2;
  const alley = courtW * 0.1;
  return {
    w,
    h,
    left,
    top,
    courtW,
    courtH,
    singlesL: left + alley,
    singlesR: left + courtW - alley,
    midY: top + courtH / 2,
    serviceH: courtH * 0.21,
    centerX: left + courtW / 2,
  };
}

function landscapeGeometry() {
  const w = 439;
  const h = 200;
  const pad = 10;
  const left = pad;
  const top = pad;
  const courtW = w - pad * 2;
  const courtH = h - pad * 2;
  const alley = courtH * 0.1;
  return {
    w,
    h,
    left,
    top,
    courtW,
    courtH,
    singlesT: top + alley,
    singlesB: top + courtH - alley,
    midX: left + courtW / 2,
    serviceW: courtW * 0.21,
    centerY: top + courtH / 2,
  };
}

export function PortraitTennisCourtSvg({ surface: surfaceProp } = {}) {
  const surface = normalizeTennisCourtColour(surfaceProp);
  const g = portraitGeometry();
  const line = surfaceLine(surface);
  const net = surfaceNet(surface);
  const idPrefix = `tp-${surface}`;
  return (
    <svg className="court-svg" viewBox={`0 0 ${g.w} ${g.h}`} preserveAspectRatio="none" aria-hidden>
      <CourtSurfaceDefs surface={surface} idPrefix={idPrefix} stripeAlong="x" />
      <rect width={g.w} height={g.h} fill={`url(#${idPrefix}-surround)`} />
      <CourtPlayingArea
        surface={surface}
        idPrefix={idPrefix}
        left={g.left}
        top={g.top}
        courtW={g.courtW}
        courtH={g.courtH}
        line={line}
      />
      <line x1={g.singlesL} y1={g.top} x2={g.singlesL} y2={g.top + g.courtH} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.singlesR} y1={g.top} x2={g.singlesR} y2={g.top + g.courtH} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.singlesL} y1={g.midY - g.serviceH} x2={g.singlesR} y2={g.midY - g.serviceH} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.singlesL} y1={g.midY + g.serviceH} x2={g.singlesR} y2={g.midY + g.serviceH} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.centerX} y1={g.midY - g.serviceH} x2={g.centerX} y2={g.midY + g.serviceH} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.left} y1={g.midY} x2={g.left + g.courtW} y2={g.midY} stroke={net} strokeWidth={2.5} strokeLinecap="round" />
      <line x1={g.centerX} y1={g.top} x2={g.centerX} y2={g.top + 9} stroke={line} strokeWidth={1.75} />
      <line x1={g.centerX} y1={g.top + g.courtH - 9} x2={g.centerX} y2={g.top + g.courtH} stroke={line} strokeWidth={1.75} />
    </svg>
  );
}

export function LandscapeTennisCourtSvg({ surface: surfaceProp } = {}) {
  const surface = normalizeTennisCourtColour(surfaceProp);
  const g = landscapeGeometry();
  const line = surfaceLine(surface);
  const net = surfaceNet(surface);
  const idPrefix = `tl-${surface}`;
  return (
    <svg className="court-svg" viewBox={`0 0 ${g.w} ${g.h}`} preserveAspectRatio="none" aria-hidden>
      <CourtSurfaceDefs surface={surface} idPrefix={idPrefix} stripeAlong="y" />
      <rect width={g.w} height={g.h} fill={`url(#${idPrefix}-surround)`} />
      <CourtPlayingArea
        surface={surface}
        idPrefix={idPrefix}
        left={g.left}
        top={g.top}
        courtW={g.courtW}
        courtH={g.courtH}
        line={line}
      />
      <line x1={g.left} y1={g.singlesT} x2={g.left + g.courtW} y2={g.singlesT} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.left} y1={g.singlesB} x2={g.left + g.courtW} y2={g.singlesB} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.midX - g.serviceW} y1={g.singlesT} x2={g.midX - g.serviceW} y2={g.singlesB} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.midX + g.serviceW} y1={g.singlesT} x2={g.midX + g.serviceW} y2={g.singlesB} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.midX - g.serviceW} y1={g.centerY} x2={g.midX + g.serviceW} y2={g.centerY} stroke={line} strokeWidth={1.35} strokeOpacity={0.95} />
      <line x1={g.midX} y1={g.top} x2={g.midX} y2={g.top + g.courtH} stroke={net} strokeWidth={2.5} strokeLinecap="round" />
      <line x1={g.left} y1={g.centerY} x2={g.left + 9} y2={g.centerY} stroke={line} strokeWidth={1.75} />
      <line x1={g.left + g.courtW - 9} y1={g.centerY} x2={g.left + g.courtW} y2={g.centerY} stroke={line} strokeWidth={1.75} />
    </svg>
  );
}
